import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, LogOut, User as UserIcon, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getMyOrders } from "../api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch (e) {
    return iso;
  }
};

const Pill = ({ children, tone = "muted" }) => {
  const tones = {
    paid: "text-green-400 border-green-500/40",
    pending: "text-amber-400 border-amber-500/40",
    muted: "text-[#a29a8c] border-[#2c2822]",
  };
  return <span className={`inline-block border rounded-full px-3 py-1 text-xs capitalize ${tones[tone] || tones.muted}`}>{children}</span>;
};

const Account = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMyOrders()
      .then((data) => setOrders(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const doLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-16" data-testid="account-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <p className="text-[#c9a24b] text-sm tracking-[0.3em] uppercase mb-2">My Account</p>
          <h1 className="font-display text-4xl text-[#f4efe4]">Hello, {user?.name}</h1>
        </div>
        <div className="flex items-center gap-3">
          {user?.role === "admin" && (
            <Link to="/admin/orders" className="btn-outline-gold px-4 py-2 rounded-full text-xs tracking-[0.15em] uppercase inline-flex items-center gap-2">
              <ShieldCheck size={14} /> Admin
            </Link>
          )}
          <button onClick={doLogout} data-testid="account-logout" className="text-[#a29a8c] hover:text-[#c9a24b] transition-colors inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em]">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="bg-[#141210] border border-[#2c2822] rounded-2xl p-6 h-fit">
          <div className="w-12 h-12 rounded-full border border-[#c9a24b]/40 flex items-center justify-center mb-4">
            <UserIcon size={20} className="text-[#c9a24b]" />
          </div>
          <h2 className="font-display text-xl text-[#f4efe4] mb-4">Profile</h2>
          <p className="text-[#6b6459] text-xs uppercase tracking-[0.12em]">Name</p>
          <p className="text-[#f4efe4] mb-3">{user?.name}</p>
          <p className="text-[#6b6459] text-xs uppercase tracking-[0.12em]">Email</p>
          <p className="text-[#f4efe4] mb-3 break-all">{user?.email}</p>
          <p className="text-[#6b6459] text-xs uppercase tracking-[0.12em]">Member since</p>
          <p className="text-[#f4efe4]">{fmtDate(user?.created_at)}</p>
        </div>

        <div className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-5">
            <Package size={18} className="text-[#c9a24b]" />
            <h2 className="font-display text-xl text-[#f4efe4]">Order History</h2>
          </div>

          {loading ? (
            <p className="text-[#a29a8c]">Loading your orders…</p>
          ) : orders.length === 0 ? (
            <div className="bg-[#141210] border border-[#2c2822] rounded-2xl p-10 text-center" data-testid="no-orders">
              <p className="text-[#a29a8c] mb-6">You haven't placed any orders yet.</p>
              <Link to="/shop" className="btn-gold inline-flex px-8 py-3 rounded-full text-sm tracking-[0.15em] uppercase">Start Shopping</Link>
            </div>
          ) : (
            <div className="space-y-4" data-testid="orders-list">
              {orders.map((o) => {
                const total = o.total || o.subtotal || 0;
                const paid = o.payment_status === "paid";
                return (
                  <div key={o.id} className="bg-[#141210] border border-[#2c2822] rounded-2xl p-6" data-testid={`account-order-${o.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      <div>
                        <span className="font-mono text-[#f4efe4]">#{(o.id || "").slice(0, 8).toUpperCase()}</span>
                        <span className="text-[#6b6459] text-sm ml-3">{fmtDate(o.createdAt)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Pill tone={paid ? "paid" : "pending"}>{o.payment_status || "pending"}</Pill>
                        {o.fulfillment_status && <Pill>{o.fulfillment_status}</Pill>}
                      </div>
                    </div>
                    <ul className="space-y-1.5 mb-4">
                      {(o.items || []).map((it, i) => (
                        <li key={i} className="flex justify-between text-[#c8c0b2] text-sm">
                          <span>{it.qty} × {it.name}</span>
                          <span className="text-[#c9a24b]">${(it.price * it.qty).toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex justify-between border-t border-[#2c2822] pt-3 text-[#f4efe4]">
                      <span>Total</span>
                      <span className="text-[#c9a24b] font-display text-lg">${Number(total).toFixed(2)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Account;
